import { useEffect } from 'react'
import { ApolloError } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { useLazyGetCheckoutRule } from '../../graphQL/queries/getCheckoutRule'
import type { IGetCheckoutRuleResponse } from '../../graphQL/queries/getCheckoutRule/types'
import { setAvailable, setLoadingState } from '../../redux/slices/checkout'
import type { CheckoutRuleDocumentType } from '../../interfaces'
import { generateDataDogContext } from '../../utils/dataDog'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'

const FUNCTION_NAME = 'useCheckoutRuleOptions'

/**
 * Fetches one rule type's options on mount and lands them in `checkoutSlice`.
 *
 * The behaviour half of a picker screen. Its template is a `Default` delegate
 * like every other page here, so this is where the screen's work lives — and
 * there is only one job: ask what the storefront offers. The picker's own
 * props are injected at prop-resolution time (`getPaymentMethodSelectorProps`)
 * rather than returned from here, because the component tree is authored in
 * SCS config and this hook has nothing to hand them to.
 *
 * **Written once for both rule types on purpose.** The delivery and payment
 * screens ask the same question of the same resolver and differ only in the
 * argument, so a per-screen copy would be two places to fix the session guard
 * below. What genuinely differs between the two screens is the *write* path —
 * the toast copy, the action names — and that lives in each screen's own
 * actions hook.
 *
 * Mount it once per screen. Two instances would issue two fetches and race
 * each other's `loadingState` writes.
 */
export const useCheckoutRuleOptions = (ruleType: CheckoutRuleDocumentType) => {
  const dispatch = useAppDispatch()
  const sessionId = useAppSelector((state) => state.defaultSlice.sessionId)
  const storeName = useAppSelector((state) => state.defaultSlice.storeName)

  /**
   * A response with no rule document reads as "offers nothing", not as a
   * failure.
   *
   * `getCheckoutRule` is nullable on the schema, but a storefront that authors
   * no rule of this type does **not** arrive here — that comes back as a `404`
   * on the error path, because the resolver reports a miss as an error rather
   * than as empty data. So `null` here is an undocumented shape, and `[]` is
   * the honest rendering of it: the shopper gets the empty-state message,
   * which is true either way and is the only thing they could act on.
   */
  const onCompleted = (response: IGetCheckoutRuleResponse) => {
    dispatch(
      setAvailable({ ruleType, data: response.getCheckoutRule?.data ?? [] }),
    )
  }

  /**
   * Keeps whatever was already loaded and raises the error flag beside it.
   *
   * `setLoadingState` does not blank `data`, so a failed refetch leaves the
   * shopper looking at the previous options rather than an empty screen. On a
   * first fetch there is nothing to keep and the screen shows the error alone.
   */
  const onError = (error: ApolloError) => {
    dispatch(
      setLoadingState({ ruleType, loadingState: 'error', error: error.message }),
    )

    const context = generateDataDogContext({
      title: 'getCheckoutRule query throws an exception',
      extraInfo: { function: FUNCTION_NAME, ruleType },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  const [fetchRule] = useLazyGetCheckoutRule({ onCompleted, onError })

  /**
   * Fetch on mount, and again if the session or the storefront moves under a
   * mounted screen.
   *
   * `sessionId` is a dependency and a guard, not merely a value read. It is
   * dispatched from an effect in `Config`, and effects run children-first, so
   * on the commit that first renders this screen it is still `undefined` here.
   * Fetching then would send `sessionUid: undefined`, which Apollo drops from
   * the request body — a variable the schema declares `ID!` — and the shopper
   * would get an error screen over options that were about to load.
   *
   * `undefined` is therefore read as "not known yet" and skipped. `''` is read
   * as a session that exists and is unusable, and is passed through, so the
   * server's rejection reaches `onError` and the screen says something. The
   * same split `useOrderDetail` makes.
   *
   * ⚠️ **No ref, and `StrictMode` is not a reason to add one.** React 18
   * double-invokes mount effects only on a concurrent root, and `index.tsx`
   * still mounts through the legacy `ReactDOM.render`. The suite is the
   * stricter environment — `@testing-library/react` renders through
   * `createRoot` — so a test wrapping this in `<StrictMode>` sees two fetches
   * while the app sees one.
   */
  useEffect(() => {
    if (sessionId === undefined) return

    dispatch(setLoadingState({ ruleType, loadingState: 'loading' }))
    fetchRule({
      variables: {
        storefrontName: storeName || '',
        ruleType,
        sessionUid: sessionId,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleType, sessionId, storeName])
}

export default useCheckoutRuleOptions
